import { useState, useEffect, useRef } from 'preact/hooks'
import { h, render, VNode } from 'preact'
import '@/components/helpers/gm-state.types'

const STORAGE_KEY = 'image-view-preference'
const IMAGE_VIEW_CHANGED_EVENT = 'image-view-preference:changed'

function getGridView(el: HTMLElement | null): HTMLElement | null {
  const fromClosest = el?.closest?.('[data-small-image]')
  if (fromClosest) return fromClosest as HTMLElement
  return document.querySelector<HTMLElement>('[data-small-image]')
}

function getInitialPreference(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'true' ? 'true' : ''
  } catch {
    return ''
  }
}

interface ImageViewToggleButtonProps {
  className?: string
}

const GRID_OPTIONS: { value: string; label: string; labelXXl: string }[] = [
  { value: '', label: '3', labelXXl: '4' },
  { value: 'true', label: '4', labelXXl: '5' }
]

export function ImageViewToggleButton({ className = '' }: ImageViewToggleButtonProps): VNode<any> {
  const rootRef = useRef<HTMLElement | null>(null)
  const [smallImage, setSmallImage] = useState('')

  useEffect(() => {
    const grid = getGridView(rootRef.current)
    if (!grid) return
    const initial = getInitialPreference()
    grid.setAttribute('data-small-image', initial)
    setSmallImage(initial)

    const handleSync = (e: CustomEvent<{ value: string }>) => {
      if (e.detail?.value !== undefined) setSmallImage(e.detail.value)
    }
    window.addEventListener(IMAGE_VIEW_CHANGED_EVENT, handleSync as EventListener)
    return () => window.removeEventListener(IMAGE_VIEW_CHANGED_EVENT, handleSync as EventListener)
  }, [])

  const setPreference = (next: string) => {
    if (next === smallImage) return
    const grid = getGridView(rootRef.current)
    if (!grid) return
    grid.setAttribute('data-small-image', next)
    setSmallImage(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent(IMAGE_VIEW_CHANGED_EVENT, { detail: { value: next } }))
  }

  return h(
    'div',
    {
      ref: rootRef as any,
      className,
      role: 'group',
      'aria-label': 'Product grid columns'
    },
    [
      h('span', null, 'Product Grid:'),
      ...GRID_OPTIONS.map((option) => {
        const isActive = smallImage === option.value
        return h(
          'button',
          {
            type: 'button',
            class: isActive
              ? 'px-0.375 text-primary text-line text-line-large link-fade'
              : 'px-0.375 text-type-black-secondary link-fade',
            onClick: () => setPreference(option.value),
            'aria-pressed': isActive,
            'data-active': isActive ? 'true' : null
          },
          [
            h('span', { class: 'xxl:hidden' }, option.label),
            h('span', { class: 'hidden xxl:inline' }, option.labelXXl)
          ]
        )
      })
    ]
  )
}

export class ImageViewToggleButtonElement extends HTMLElement {
  connectedCallback(): void {
    render(
      h(ImageViewToggleButton, {
        className: this.getAttribute('data-class') || ''
      }),
      this
    )
  }
}
